import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { UserList } from './UserList';
import { UserSearch } from './UserSearch';
import { UserFilters } from './UserFilters';
import { UserOrganizationFilters } from './UserOrganizationFilters';
import { Button } from '@/components/ui/button';
import { Plus, Settings2, Trash2 } from 'lucide-react';
import { AddUserDialog } from './AddUserDialog';
import { UserSettingsDialog } from './UserSettingsDialog';
import { DeletionRequestsDialog } from './DeletionRequestsDialog';
import { useLanguage } from '@/contexts/LanguageContext';

export function UserManagement() {
  const [showAddUser, setShowAddUser] = useState(false);
  const [showUserSettings, setShowUserSettings] = useState(false);
  const [showDeletionRequests, setShowDeletionRequests] = useState(false);
  const [pendingDeletions, setPendingDeletions] = useState(0);

  useEffect(() => {
    const q = query(collection(db, 'accountDeletionRequests'), where('status', '==', 'pending'));
    return onSnapshot(q, (snap) => setPendingDeletions(snap.size));
  }, []);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string[]>([]);
  const [organizationFilter, setOrganizationFilter] = useState<string[]>([]);
  const { t, dir, language } = useLanguage();
  const isRTL = language === 'ar';

  return (
    <div className="space-y-6" dir={dir}>
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-1">
          <h2 className="text-2xl font-bold tracking-tight">
            {t('dashboard.admin.users.title')}
          </h2>
          <p className="text-muted-foreground">
            {t('dashboard.admin.users.description')}
          </p>
        </div>
        <div className={`flex gap-2 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
          <Button onClick={() => setShowAddUser(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            <span className="hidden md:inline">{t('dashboard.admin.users.addUser')}</span>
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setShowUserSettings(true)}
            className="gap-2"
          >
            <Settings2 className="h-4 w-4" />
            <span className="hidden md:inline">{isRTL ? 'إعدادات المستخدمين' : 'Users Settings'}</span>
          </Button>
          <div className="relative">
            <Button 
              variant="outline" 
              onClick={() => setShowDeletionRequests(true)}
              className="gap-2"
            >
              <Trash2 className="h-4 w-4" />
              <span className="hidden md:inline">{isRTL ? 'طلبات الحذف' : 'Deletion Requests'}</span>
            </Button>
            {pendingDeletions > 0 && (
              <span className="absolute -top-2 -right-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold shadow-sm">
                {pendingDeletions}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4 md:flex-row md:items-center">
        <UserSearch 
          value={searchQuery} 
          onChange={setSearchQuery}
          placeholder={t('dashboard.admin.users.search')}
        />
        <UserFilters 
          selected={roleFilter} 
          onSelect={setRoleFilter}
          title={t('dashboard.admin.users.filters.title')}
        />
        <UserOrganizationFilters 
          selected={organizationFilter} 
          onSelect={setOrganizationFilter}
          title={t('dashboard.admin.users.filters.organizationsTitle')}
        />
      </div>

      <UserList searchQuery={searchQuery} roleFilter={roleFilter} organizationFilter={organizationFilter} />

      <AddUserDialog 
        open={showAddUser}
        onOpenChange={setShowAddUser}
      />

      <UserSettingsDialog
        open={showUserSettings}
        onOpenChange={setShowUserSettings}
      />

      <DeletionRequestsDialog
        open={showDeletionRequests}
        onOpenChange={setShowDeletionRequests}
      />
    </div>
  );
}